/**
 * Stops transferred onto this worker, from today forward.
 *
 * The route already shows them. This is only the notice: which rows are new,
 * which ones the dashboard has already greeted, and which ones they have
 * acknowledged. Receipts live in localStorage under the user id, same as
 * estimate responses — this browser, this person, not the account.
 *
 * RD-NOTICES-V2: the acked/announced id SETS below also write-through to the
 * server as `route_change:<id>` (announced_at / acked_at). Unlike the
 * timestamp-map stores, these sets carry no per-id write time locally, so the
 * sync helper stamps "now" at sync time — read fresh from storage right after
 * the local write, so it reflects true membership rather than only the ids
 * just touched. This is UNION semantics (an id can only move id→member, never
 * back out), which is trivially monotonic. The customer-NAME cache below is
 * NOT part of this — it stays device-local and is untouched by this feature.
 */
import { upsertNoticeReceipts } from '../../services/noticeReceipts';
import { tenantIdForNoticeWrite } from '../notices/noticeReceiptsSession';
import { formatPersonName } from '../format/formatPersonName';

const ROUTE_CHANGE_PREFIX = 'route_change:';

const ACK_PREFIX = 'cvx-worker-transfer-acked-v1';
const ANNOUNCED_PREFIX = 'cvx-worker-transfer-announced-v1';
const NAME_CACHE_PREFIX = 'cvx-worker-transfer-known-v1';
// Receipts must hold a worker's WHOLE book: the `assigned:<id>` baseline
// marks every customer on the route known. A cap of 80 evicted the oldest ids,
// so a worker with >80 customers got false "Nuevo en tu ruta" rows on the next
// launch and it never settled (audit 2026-09-26). Ids only — ~90KB at the cap.
const MAX_IDS = 2000;
// Device-local name/status cache (PII, cleared at sign-out): sized to a
// realistic route book so a removed customer still shows its name.
const MAX_NAMES = 500;

function storageKey(prefix, userId) {
  return `${prefix}:${userId}`;
}

// `assigned:` / `unassigned:` ids are the worker's customer BASELINE — evicting
// one makes an old customer re-surface as "Nuevo en tu ruta". They are never
// evicted (bounded by the route book itself). Only per-event ids (transfers,
// reschedules — each tied to a date that passes) roll off, oldest first.
const PROTECTED_ID_PREFIXES = ['assigned:', 'unassigned:'];
const MIN_EVENT_ROOM = 500;
export function capReceiptIds(ids) {
  const isProtected = (id) => PROTECTED_ID_PREFIXES.some((p) => id.startsWith(p));
  const kept = ids.filter(isProtected);
  const events = ids.filter((id) => !isProtected(id));
  const room = Math.max(MAX_IDS - kept.length, MIN_EVENT_ROOM);
  return [...kept, ...events.slice(-room)];
}

// No userId → no per-user bucket to read or write. Reads answer empty (never
// a shared `:anon` bucket seen by every logged-out/undetermined caller);
// writes are a silent no-op.
function readIds(prefix, userId, storage) {
  if (!userId) return new Set();
  try {
    const raw = (storage || window.localStorage).getItem(storageKey(prefix, userId));
    const parsed = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(parsed)) return new Set();
    return new Set(capReceiptIds(parsed.filter((id) => typeof id === 'string' && id)));
  } catch {
    return new Set();
  }
}

function writeIds(prefix, userId, ids, storage) {
  if (!userId) return false;
  try {
    const store = storage || window.localStorage;
    store.setItem(storageKey(prefix, userId), JSON.stringify(capReceiptIds(Array.from(ids))));
    return true;
  } catch {
    return false;
  }
}

export function readWorkerTransferAcked(userId, storage) {
  return readIds(ACK_PREFIX, userId, storage);
}

export function readWorkerTransferAnnounced(userId, storage) {
  return readIds(ANNOUNCED_PREFIX, userId, storage);
}

function syncRouteChangeReceipt(userId, id, storage) {
  const tenantId = tenantIdForNoticeWrite(userId);
  if (!tenantId) return;
  const nowIso = new Date().toISOString();
  const ackedNow = readWorkerTransferAcked(userId, storage).has(id);
  const announcedNow = readWorkerTransferAnnounced(userId, storage).has(id);
  upsertNoticeReceipts({
    tenantId,
    userId,
    rows: [{
      notice_key: `${ROUTE_CHANGE_PREFIX}${id}`,
      announced_at: announcedNow ? nowIso : null,
      acked_at: ackedNow ? nowIso : null,
    }],
  });
}

export function acknowledgeWorkerTransfers(userId, ids, storage, opts = {}) {
  const next = readWorkerTransferAcked(userId, storage);
  (ids || []).forEach((id) => { if (id) next.add(String(id)); });
  writeIds(ACK_PREFIX, userId, next, storage);
  announceWorkerTransfers(userId, ids, storage, { skipSync: true });
  if (!opts.skipSync) (ids || []).forEach((id) => { if (id) syncRouteChangeReceipt(userId, String(id), storage); });
  return next;
}

export function announceWorkerTransfers(userId, ids, storage, opts = {}) {
  const next = readWorkerTransferAnnounced(userId, storage);
  (ids || []).forEach((id) => { if (id) next.add(String(id)); });
  writeIds(ANNOUNCED_PREFIX, userId, next, storage);
  if (!opts.skipSync) (ids || []).forEach((id) => { if (id) syncRouteChangeReceipt(userId, String(id), storage); });
  return next;
}

/** notice_key namespace check — used by the sync registry to route incoming
 *  server rows to this store. */
export function ownsRouteChangeNoticeKey(noticeKey) {
  return typeof noticeKey === 'string' && noticeKey.startsWith(ROUTE_CHANGE_PREFIX);
}

/** Server rows → local id sets. UNION, not max: an id can only move from
 *  "unknown" to "announced"/"acked", never back — so applying every server
 *  row unconditionally is already monotonic. */
export function mergeRouteChangeReceiptsFromServer(userId, rows, storage) {
  if (!userId || !Array.isArray(rows)) return;
  const toAnnounce = [];
  const toAck = [];
  rows.forEach((row) => {
    if (!ownsRouteChangeNoticeKey(row?.notice_key)) return;
    const id = row.notice_key.slice(ROUTE_CHANGE_PREFIX.length);
    if (!id) return;
    if (row.announced_at) toAnnounce.push(id);
    if (row.acked_at) toAck.push(id);
  });
  if (toAnnounce.length) announceWorkerTransfers(userId, toAnnounce, storage, { skipSync: true });
  if (toAck.length) acknowledgeWorkerTransfers(userId, toAck, storage, { skipSync: true });
}

/** Snapshot for the one-time post-hydration upload. */
export function collectRouteChangeReceiptsForUpload(userId, storage) {
  if (!userId) return [];
  const acked = readWorkerTransferAcked(userId, storage);
  const announced = readWorkerTransferAnnounced(userId, storage);
  const ids = new Set([...acked, ...announced]);
  const nowIso = new Date().toISOString();
  return Array.from(ids).map((id) => ({
    notice_key: `${ROUTE_CHANGE_PREFIX}${id}`,
    announced_at: announced.has(id) ? nowIso : null,
    acked_at: acked.has(id) ? nowIso : null,
  }));
}

/**
 * Active overrides whose cover worker is them and whose day is today or later.
 * Today is urgent. A later day is a quieter notice. A past day is gone.
 * A stop taken off their route (they are the source) is not a notice.
 * A cancelled transfer is gone.
 */
export function selectIncomingTransfers(overrides, customersById, { workerId, today } = {}) {
  if (!workerId || !today) return [];
  return (overrides || [])
    .filter((ov) => ov
      && !ov.cancelled_at
      && ov.target_worker_id === workerId
      && typeof ov.target_date === 'string'
      && ov.target_date >= today)
    .map((ov) => ({
      id: String(ov.id),
      customerName: formatPersonName(customersById?.[ov.customer_id]?.full_name) || '',
      targetDate: ov.target_date,
      urgent: ov.target_date === today,
    }))
    .sort((a, b) => a.targetDate.localeCompare(b.targetDate)
      || a.customerName.localeCompare(b.customerName));
}

// ---------------------------------------------------------------------------
// Customer-name cache for `removed` rows.
//
// After a cancel, RLS no longer lets the covering worker read that customer
// (the override that granted the read is gone). We remember the name we saw
// while the row was active — this browser, this person, PII on a possibly
// shared device, so it gets its own prefix and its own clear function
// (separate from the acked/announced id sets, which are UUIDs only and must
// survive a name-cache clear so we don't re-greet everything).
function nameCacheKey(userId) {
  return `${NAME_CACHE_PREFIX}:${userId}`;
}

function readNameCache(userId, storage) {
  if (!userId) return {};
  try {
    const raw = (storage || window.localStorage).getItem(nameCacheKey(userId));
    const parsed = raw ? JSON.parse(raw) : {};
    return (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) ? parsed : {};
  } catch {
    return {};
  }
}

function writeNameCache(userId, map, storage) {
  if (!userId) return false;
  try {
    const store = storage || window.localStorage;
    store.setItem(nameCacheKey(userId), JSON.stringify(map));
    return true;
  } catch {
    return false;
  }
}

/** Read the cached name for one override id, or ''. */
export function readWorkerTransferKnownName(userId, id, storage) {
  if (!userId || !id) return '';
  const entry = readNameCache(userId, storage)[String(id)];
  return entry?.name || '';
}

/**
 * Remember `id → name` (seen while the row was active). Prunes entries whose
 * date is before `today` and caps at MAX_NAMES most-recently-written.
 */
export function rememberWorkerTransferName(userId, id, name, date, storage) {
  if (!userId || !id || !name) return;
  const cache = readNameCache(userId, storage);
  const today = date || null;
  const pruned = {};
  Object.keys(cache).forEach((key) => {
    const entry = cache[key];
    if (!entry) return;
    if (today && entry.date && entry.date < today) return;
    pruned[key] = entry;
  });
  delete pruned[String(id)];
  const keys = Object.keys(pruned);
  if (keys.length >= MAX_NAMES) {
    // Drop the oldest (first-inserted) entries to make room.
    keys.slice(0, keys.length - MAX_NAMES + 1).forEach((k) => delete pruned[k]);
  }
  pruned[String(id)] = { name: String(name), date: date || null };
  writeNameCache(userId, pruned, storage);
}

/** Remove every stored customer-name cache (all users) — sign-out, shared device. */
export function clearWorkerTransferNameCache(storage) {
  try {
    const store = storage || window.localStorage;
    const keys = [];
    for (let i = 0; i < store.length; i += 1) {
      const key = store.key(i);
      if (key && key.startsWith(`${NAME_CACHE_PREFIX}:`)) keys.push(key);
    }
    keys.forEach((key) => store.removeItem(key));
  } catch { /* best effort */ }
}

// ---------------------------------------------------------------------------
// Customer-name/next-visit/status cache for RD-ROLE-009 `assigned`/`unassigned`
// rows. Same storage bucket, same cap, same sign-out clear (clearWorkerTransferNameCache
// above already sweeps every key under NAME_CACHE_PREFIX regardless of shape) —
// distinguished only by a `cust:<id>` key so it can never collide with an
// override-id entry. Unlike `rememberWorkerTransferName`, entries here carry
// `nextVisit` (not `date`) and are never date-pruned by that function's prune
// loop (it only ever inspects `entry.date`) — eviction is MAX_NAMES-only,
// which is intentional: an `unassigned` row still needs the LAST known
// nextVisit/status even after that date has passed. `status` (the cached
// lifecycle_status) stays device-local like the rest of this cache — never
// sent in a server receipt — and backs the `unassigned` urgency test (an
// opening/closing customer dropping off the route is pending work, so it
// stays urgent even without a today-dated nextVisit).
function custCacheKey(customerId) {
  return `cust:${customerId}`;
}

/** Remember (or refresh) `{name, nextVisit, status}` for an assigned customer. */
export function rememberAssignedCustomer(userId, customerId, name, nextVisit, status, storage) {
  if (!userId || !customerId || !name) return;
  const cache = readNameCache(userId, storage);
  const key = custCacheKey(customerId);
  if (!(key in cache)) {
    const keys = Object.keys(cache);
    if (keys.length >= MAX_NAMES) {
      keys.slice(0, keys.length - MAX_NAMES + 1).forEach((k) => delete cache[k]);
    }
  }
  cache[key] = { name: String(name), nextVisit: nextVisit || null, status: status || null };
  writeNameCache(userId, cache, storage);
}

/** Read `{name, nextVisit, status}` for a customer id, or null. */
export function readAssignedCustomerCache(userId, customerId, storage) {
  if (!userId || !customerId) return null;
  const entry = readNameCache(userId, storage)[custCacheKey(customerId)];
  return entry ? { name: entry.name || '', nextVisit: entry.nextVisit || null, status: entry.status || null } : null;
}

export const KIND_ORDER = {
  added: 0, moved: 1, assigned: 2, away: 3, removed: 4, unassigned: 5, returned: 6,
};

/** Shared date → kind → name sort for every notice-row family. */
export function sortNoticeRows(rows) {
  return (rows || []).slice().sort((a, b) => {
    const dateA = a.targetDate || '';
    const dateB = b.targetDate || '';
    return dateA.localeCompare(dateB)
      || (KIND_ORDER[a.kind] ?? 99) - (KIND_ORDER[b.kind] ?? 99)
      || (a.customerName || '').localeCompare(b.customerName || '');
  });
}

/**
 * Every route-change row this worker should be told about: stops added to
 * their route, stops moved away, stops removed after a cancelled add, and
 * stops returned after a cancelled away. See workerTransferNotice.js header.
 *
 * @param {object[]} overrides active route_overrides (target/source = me)
 * @param {object[]} cancelled cancelled route_overrides (target/source = me)
 * @param {object} customersById
 * @param {object} opts
 * @param {string} opts.workerId
 * @param {string} opts.today YYYY-MM-DD
 * @param {{ announced: Set<string>, acked: Set<string> }} opts.told
 * @param {{ userId: string, storage?: any }} [opts.names] when given, reads
 *   the localStorage name cache as a fallback for `removed` rows.
 */
export function selectRouteChanges(overrides, cancelled, customersById, opts = {}) {
  const { workerId, today, told, names } = opts;
  if (!workerId || !today) return [];
  const told_ = told || { announced: new Set(), acked: new Set() };
  const known = (id) => told_.announced.has(id) || told_.acked.has(id);

  const nameFor = (ov) => customersById?.[ov.customer_id]?.full_name
    || ov.customer?.full_name
    || '';

  const rows = [];

  (overrides || []).forEach((ov) => {
    if (!ov || ov.cancelled_at) return;
    if (typeof ov.target_date !== 'string' || ov.target_date < today) return;
    if (ov.target_worker_id === workerId) {
      rows.push({
        id: String(ov.id),
        overrideId: ov.id,
        kind: 'added',
        customerName: formatPersonName(nameFor(ov)),
        targetDate: ov.target_date,
        urgent: ov.target_date === today,
      });
    } else if (ov.source_worker_id === workerId) {
      rows.push({
        id: `${ov.id}:away`,
        overrideId: ov.id,
        kind: 'away',
        customerName: formatPersonName(nameFor(ov)),
        targetDate: ov.target_date,
        urgent: ov.target_date === today,
      });
    }
  });

  (cancelled || []).forEach((ov) => {
    if (!ov || !ov.cancelled_at) return;
    if (typeof ov.target_date !== 'string' || ov.target_date < today) return;
    if (ov.target_worker_id === workerId) {
      const addKey = String(ov.id);
      if (!known(addKey)) return; // never told about the add — net zero
      const cachedName = names?.userId
        ? readWorkerTransferKnownName(names.userId, ov.id, names.storage)
        : '';
      rows.push({
        id: `${ov.id}:removed`,
        overrideId: ov.id,
        kind: 'removed',
        customerName: formatPersonName(cachedName || nameFor(ov)),
        targetDate: ov.target_date,
        urgent: ov.target_date === today,
      });
    } else if (ov.source_worker_id === workerId) {
      const awayKey = `${ov.id}:away`;
      if (!known(awayKey)) return; // never told about the away — net zero
      rows.push({
        id: `${ov.id}:returned`,
        overrideId: ov.id,
        kind: 'returned',
        customerName: formatPersonName(nameFor(ov)),
        targetDate: ov.target_date,
        urgent: ov.target_date === today,
      });
    }
  });

  return rows.sort((a, b) => a.targetDate.localeCompare(b.targetDate)
    || (KIND_ORDER[a.kind] - KIND_ORDER[b.kind])
    || a.customerName.localeCompare(b.customerName));
}
